import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { COLORS } from '../../Styles/Colors';
import { FORM } from '../Form/formStyles';

interface Location {
  OVID: number | string;
  name: string;
  CID?: string;
  CustomerName?: string;
  CustomerId?: string | number;
  ShipToId?: string;
}

interface ChecklistItemProps {
  disabled?: boolean;
}

interface CheckboxProps {
  disabled?: boolean;
}

interface GroupHeaderProps extends ChecklistItemProps {
  hasSelected?: boolean;
}

interface LocationChecklistProps {
  name?: string;
  selectedValues?: number[];
  onChange: (event: { target: { name: string; value: number[] } }) => void;
  ovs?: Location[];
  loadingOvs?: boolean;
  disabled?: boolean;
  emptyMessage?: string;
}

const toOvid = (value: unknown): number => Number(value);

const ChecklistContainer = styled.div`
  border: 1px solid ${FORM.border};
  border-radius: ${FORM.fieldRadius};
  max-height: 340px;
  overflow-y: auto;
  background-color: ${COLORS.white};
`;

const ChecklistItem = styled.label<ChecklistItemProps>`
  display: flex;
  align-items: center;
  padding: 8px 12px 8px 36px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  border-bottom: 1px solid ${FORM.border};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    background-color: ${(props) => (props.disabled ? 'transparent' : `${COLORS.teal}10`)};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Checkbox = styled.input<CheckboxProps>`
  margin-right: 8px;
  flex-shrink: 0;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  width: 16px;
  height: 16px;
  accent-color: ${COLORS.teal};

  &:focus-visible {
    outline: 2px solid ${COLORS.teal}50;
    outline-offset: 2px;
  }
`;

const LocationText = styled.span`
  flex: 1;
  font-size: 14px;
  color: ${COLORS.darkGray};
`;

const LoadingItem = styled.div`
  padding: 20px 12px;
  text-align: center;
  color: ${COLORS.gray};
  font-size: 0.875rem;
`;

const GroupHeader = styled.div<GroupHeaderProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  background-color: ${(props) => (props.hasSelected ? `${COLORS.teal}10` : '#f8f9fa')};
  border-bottom: 1px solid ${FORM.border};
  font-weight: 600;
  user-select: none;
  border-left: 3px solid ${(props) => (props.hasSelected ? COLORS.teal : 'transparent')};

  &:hover {
    background-color: ${(props) => {
      if (props.disabled) return props.hasSelected ? `${COLORS.teal}10` : '#f8f9fa';
      return props.hasSelected ? `${COLORS.teal}18` : `${COLORS.teal}10`;
    }};
  }
`;

const ChevronWrap = styled.span`
  display: flex;
  color: ${COLORS.gray};
  flex-shrink: 0;
`;

const SelectionIndicator = styled.span`
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 10px;
  font-weight: 600;
  margin-left: 8px;
  white-space: nowrap;
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const CustomerNameText = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: ${COLORS.darkGray};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LocationCountLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${COLORS.lightGray};
  white-space: nowrap;
`;

const HeaderBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  background: ${COLORS.white};
  border-bottom: 1px solid ${FORM.border};
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SearchWrap = styled.div`
  position: relative;
`;

const SearchIconWrap = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  color: ${COLORS.lightGray};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px 8px 32px;
  border: 1px solid ${FORM.border};
  border-radius: ${FORM.fieldRadius};
  font-size: 13px;
  color: ${COLORS.darkGray};
  outline: none;

  &:focus {
    border-color: ${COLORS.teal};
    box-shadow: 0 0 4px ${COLORS.teal}50;
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${COLORS.lightGray};
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 0 2px;
`;

const SelectedCount = styled.span`
  color: ${COLORS.darkGray};
  font-size: 0.75rem;
  font-weight: 600;
`;

const ToolbarActions = styled.div`
  display: flex;
  gap: 12px;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${COLORS.teal};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: ${COLORS.lightGray};
    cursor: default;
    text-decoration: none;
  }
`;

const emitChange = (
  name: string,
  value: number[],
  onChange: LocationChecklistProps['onChange']
) => {
  onChange({ target: { name, value } });
};

const LocationChecklist: React.FC<LocationChecklistProps> = ({
  name = 'OVID',
  selectedValues = [],
  onChange,
  ovs = [],
  loadingOvs = false,
  disabled = false,
  emptyMessage = 'No locations available',
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState('');

  const selectedSet = useMemo(() => new Set(selectedValues.map(toOvid)), [selectedValues]);

  const groupedByCustomer = useMemo(() => {
    const groups: Record<string, Location[]> = {};
    for (const loc of ovs) {
      const key = String(loc.CustomerId ?? loc.CID ?? '');
      if (!groups[key]) groups[key] = [];
      groups[key].push(loc);
    }
    return groups;
  }, [ovs]);

  const customerEntries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const entries = Object.keys(groupedByCustomer).map((cid) => {
      const allLocations = groupedByCustomer[cid];
      const customerName = allLocations[0]?.CustomerName || `Customer ${cid || '—'}`;
      const customerMatch =
        !term ||
        customerName.toLowerCase().includes(term) ||
        cid.toLowerCase().includes(term);

      const locations = customerMatch
        ? allLocations
        : allLocations.filter((l) => String(l.name || '').toLowerCase().includes(term));

      const selectedCount = allLocations.filter((l) => selectedSet.has(toOvid(l.OVID))).length;

      return {
        cid,
        customerName,
        locations,
        selectedCount,
        totalCount: allLocations.length,
        hasSelected: selectedCount > 0,
      };
    });

    const visible = term ? entries.filter((e) => e.locations.length > 0) : entries;

    visible.sort((a, b) => {
      if (a.hasSelected !== b.hasSelected) return b.hasSelected ? 1 : -1;
      return a.customerName.localeCompare(b.customerName);
    });

    return visible;
  }, [groupedByCustomer, searchTerm, selectedSet]);

  const visibleIds = useMemo(
    () => customerEntries.flatMap((e) => e.locations.map((l) => toOvid(l.OVID))),
    [customerEntries]
  );

  const visibleSelectedCount = visibleIds.filter((id) => selectedSet.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && visibleSelectedCount === visibleIds.length;
  const noneVisibleSelected = visibleSelectedCount === 0;
  const searchActive = searchTerm.trim().length > 0;
  const groupCount = Object.keys(groupedByCustomer).length;

  const handleCheckboxChange = (ovidValue: number): void => {
    if (disabled || loadingOvs) return;

    const next = selectedSet.has(ovidValue)
      ? selectedValues.filter((val) => toOvid(val) !== ovidValue)
      : [...selectedValues, ovidValue];

    emitChange(name, next, onChange);
  };

  const handleGroupToggle = (cid: string, locationIds: number[], e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || loadingOvs || locationIds.length === 0) return;

    const allSelected = locationIds.every((id) => selectedSet.has(id));
    const next = allSelected
      ? selectedValues.filter((val) => !locationIds.includes(toOvid(val)))
      : [...selectedValues.filter((val) => !locationIds.includes(toOvid(val))), ...locationIds];

    emitChange(name, next, onChange);
    setExpanded((prev) => ({ ...prev, [cid]: true }));
  };

  const handleSelectVisible = (select: boolean) => {
    if (disabled || loadingOvs) return;
    const visible = new Set(visibleIds);
    const next = select
      ? [...selectedValues.filter((id) => !visible.has(toOvid(id))), ...visibleIds]
      : selectedValues.filter((id) => !visible.has(toOvid(id)));
    emitChange(name, next, onChange);
  };

  const toggleExpanded = (cid: string, currentlyOpen: boolean) => {
    if (disabled) return;
    setExpanded((prev) => ({ ...prev, [cid]: !currentlyOpen }));
  };

  if (loadingOvs) {
    return (
      <ChecklistContainer>
        <LoadingItem>Loading locations...</LoadingItem>
      </ChecklistContainer>
    );
  }

  return (
    <ChecklistContainer>
      <HeaderBar>
        <SearchWrap>
          <SearchIconWrap>
            <Search size={14} />
          </SearchIconWrap>
          <SearchInput
            placeholder="Search account or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={disabled || ovs.length === 0}
            aria-label="Search locations"
          />
        </SearchWrap>
        {ovs.length > 0 && (
          <MetaRow>
            <SelectedCount>
              {selectedValues.length} of {ovs.length} selected
            </SelectedCount>
            <ToolbarActions>
              <TextButton
                type="button"
                onClick={() => handleSelectVisible(true)}
                disabled={disabled || allVisibleSelected || visibleIds.length === 0}
              >
                {searchActive ? 'Select visible' : 'Select all'}
              </TextButton>
              <TextButton
                type="button"
                onClick={() => handleSelectVisible(false)}
                disabled={disabled || noneVisibleSelected}
              >
                Clear
              </TextButton>
            </ToolbarActions>
          </MetaRow>
        )}
      </HeaderBar>
      {ovs.length === 0 ? (
        <LoadingItem>{emptyMessage}</LoadingItem>
      ) : customerEntries.length === 0 ? (
        <LoadingItem>No locations match your search.</LoadingItem>
      ) : (
        customerEntries.map(({ cid, customerName, locations, selectedCount, totalCount, hasSelected }) => {
          const userOverride = expanded[cid];
          const defaultOpen = searchActive || groupCount === 1 || hasSelected;
          const isExpanded = userOverride !== undefined ? userOverride : defaultOpen;
          const visibleGroupIds = locations.map((l) => toOvid(l.OVID));
          const groupAllSelected =
            visibleGroupIds.length > 0 && visibleGroupIds.every((id) => selectedSet.has(id));
          const groupSomeSelected = visibleGroupIds.some((id) => selectedSet.has(id));

          const sortedLocations = [...locations].sort((a, b) => {
            const aSel = selectedSet.has(toOvid(a.OVID)) ? 1 : 0;
            const bSel = selectedSet.has(toOvid(b.OVID)) ? 1 : 0;
            if (aSel !== bSel) return bSel - aSel;
            return String(a.name || '').localeCompare(String(b.name || ''));
          });

          return (
            <div key={`group-${cid}`}>
              <GroupHeader
                disabled={disabled}
                hasSelected={hasSelected}
                onClick={() => toggleExpanded(cid, isExpanded)}
              >
                <ChevronWrap>
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </ChevronWrap>
                <Checkbox
                  type="checkbox"
                  checked={groupAllSelected}
                  disabled={disabled}
                  ref={(el) => {
                    if (el) el.indeterminate = groupSomeSelected && !groupAllSelected;
                  }}
                  onClick={(e) => handleGroupToggle(cid, visibleGroupIds, e)}
                  onChange={() => undefined}
                  aria-label={`Select all locations for ${customerName}`}
                />
                <CustomerInfo>
                  <CustomerNameText>{customerName}</CustomerNameText>
                  {hasSelected && (
                    <SelectionIndicator>
                      {selectedCount} selected
                    </SelectionIndicator>
                  )}
                </CustomerInfo>
                <LocationCountLabel>
                  {searchActive && locations.length !== totalCount
                    ? `${locations.length} of ${totalCount}`
                    : `${totalCount} location${totalCount !== 1 ? 's' : ''}`}
                </LocationCountLabel>
              </GroupHeader>
              {isExpanded && (
                <div>
                  {sortedLocations.map((loc) => {
                    const ovid = toOvid(loc.OVID);
                    return (
                      <ChecklistItem key={ovid} disabled={disabled}>
                        <Checkbox
                          type="checkbox"
                          checked={selectedSet.has(ovid)}
                          onChange={() => handleCheckboxChange(ovid)}
                          disabled={disabled}
                        />
                        <LocationText>{loc.name}</LocationText>
                      </ChecklistItem>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
    </ChecklistContainer>
  );
};

export default LocationChecklist;
